/**
 * Footer Component
 * Application footer with system info, links, and branding
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface FooterLink {
  label: string;
  href?: string;
  onClick?: () => void;
}

export interface FooterSection {
  title: string;
  links: FooterLink[];
}

export interface FooterProps {
  sections?: FooterSection[];
  copyright?: string;
  logo?: ReactNode;
  logoText?: string;
  socialLinks?: {
    icon: ReactNode;
    href: string;
    label: string;
  }[];
  showPoweredBy?: boolean;
  className?: string;
}

export function Footer({
  sections = [],
  copyright,
  logo,
  logoText = 'HALMAS',
  socialLinks = [],
  showPoweredBy = true,
  className = '',
}: FooterProps) {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const defaultCopyright = `© ${currentYear} ${logoText}. Araken Sdn. Bhd. All rights reserved.`;

  return (
    <footer className={`bg-gray-50 border-t border-gray-200 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Logo & Description */}
          <div className="md:col-span-4">
            <div className="flex items-center space-x-2 mb-4">
              {logo || (
                <div className="h-8 w-8 bg-blue-600 rounded flex items-center justify-center">
                  <span className="text-white font-bold text-xl">H</span>
                </div>
              )}
              <span className="text-xl font-bold text-gray-900">{logoText}</span>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {t('app.title', 'Halal Management and Audit System')}
            </p>
            {showPoweredBy && (
              <p className="text-xs text-gray-500">
                {t('app.tagline', 'Powered by MAIS Selangor')}
              </p>
            )}

            {/* Social Links */}
            {socialLinks.length > 0 && (
              <div className="flex space-x-4 mt-6">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label={social.label}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Footer Sections */}
          {sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="md:col-span-2">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                {section.title}
              </h3>
              <ul className="space-y-3">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <a
                      href={link.href}
                      onClick={(e) => {
                        if (link.onClick) {
                          e.preventDefault();
                          link.onClick();
                        }
                      }}
                      className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Section: Copyright */}
        <div className="mt-8 pt-8 border-t border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-500">
              {copyright || defaultCopyright}
            </p>

            {/* Additional Footer Links (Privacy, Terms, etc.) */}
            <div className="flex space-x-6">
              <a
                href="#privacy"
                className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
              >
                Privacy Policy
              </a>
              <a
                href="#terms"
                className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
              >
                Terms of Service
              </a>
              <a
                href="#cookies"
                className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
              >
                Cookie Policy
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
